import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../../i18n';
import { trackActivityEvent, usePageActivityTracker } from '@services/activityTracker';

const base = import.meta.env.BASE_URL;

const MATERIALS = [
	{
		id: 'seeker',
		file: `${base}downloads/seeker.pdf`,
		icon: 'bi-house-heart',
		titleKey: 'resources.brochure.seeker.title',
		titleDefault: 'Seeker',
		descKey: 'resources.brochure.seeker.desc',
		descDefault: 'Overview for garden owners — plan your space, get offers, and bring your vision to life.',
	},
	{
		id: 'company',
		file: `${base}downloads/company.pdf`,
		icon: 'bi-building',
		titleKey: 'resources.brochure.company.title',
		titleDefault: 'Company',
		descKey: 'resources.brochure.company.desc',
		descDefault: 'Overview for landscaping companies — streamline planning, quotes, and client collaboration.',
	},
] as const;

export default function Resources() {
	const { t } = useI18n();
	usePageActivityTracker('resources');
	const videoSrc = `${base}downloads/hortera.mp4`;
	const videoTrackedRef = useRef(false);

	function trackResourceAction(action: string, resourceId: string) {
		void trackActivityEvent({
			event_name: 'resources_asset_action',
			properties: { action, resource_id: resourceId }
		});
	}

	return (
		<>
			<section className="landing-hero py-5">
				<div className="container">
					<div className="text-center mb-4">
						<h1 className="display-4 fw-bold text-success-emphasis mb-3">
							{t('resources.hero.title', 'Resources')}
						</h1>
						<p className="lead text-muted mb-0">
							{t(
								'resources.hero.subtitle',
								'Watch our introduction video and browse brochures. Share this page instead of emailing large files.'
							)}
						</p>
					</div>
				</div>
			</section>

			<section className="landing-section py-5 bg-white" id="video">
				<div className="container">
					<div className="text-center mb-4">
						<h2 className="display-6 fw-bold text-success-emphasis mb-2">
							{t('resources.video.title', 'HortEra')}
						</h2>
						<p className="text-muted lead fs-5 mb-0">
							{t('resources.video.subtitle', 'A short introduction to the HortEra platform.')}
						</p>
					</div>
					<div className="row justify-content-center">
						<div className="col-12 col-lg-10">
							<div
								className="ratio ratio-16x9 rounded-4 overflow-hidden shadow-sm border"
								style={{ background: '#000' }}
							>
								<video
									className="w-100 h-100"
									controls
									playsInline
									preload="metadata"
									poster={`${base}logo-transparent.png`}
									onPlay={() => {
										if (videoTrackedRef.current) return;
										videoTrackedRef.current = true;
										trackResourceAction('play', 'intro_video');
									}}
								>
									<source src={videoSrc} type="video/mp4" />
									{t(
										'resources.video.unsupported',
										'Your browser does not support embedded video. Download the file instead.'
									)}
								</video>
							</div>
							<div className="d-flex justify-content-center mt-3">
								<a
									href={videoSrc}
									download
									className="btn btn-outline-success"
									onClick={() => trackResourceAction('download', 'intro_video')}
								>
									<i className="bi bi-download me-2" aria-hidden="true" />
									{t('resources.video.download', 'Download video')}
								</a>
							</div>
						</div>
					</div>
				</div>
			</section>

			<section className="landing-section py-5 bg-light" id="brochures">
				<div className="container">
					<div className="text-center mb-5">
						<h2 className="display-6 fw-bold text-success-emphasis mb-2">
							{t('resources.brochures.title', 'Brochures')}
						</h2>
						<p className="text-muted lead fs-5 mb-0">
							{t(
								'resources.brochures.subtitle',
								'Open in your browser or download the PDF to share with colleagues and clients.'
							)}
						</p>
					</div>
					<div className="row g-4 justify-content-center">
						{MATERIALS.map((item) => (
							<div key={item.id} className="col-12 col-md-6 col-lg-5" id={item.id}>
								<div className="card h-100 border-0 shadow-sm">
									<div className="card-body p-4 d-flex flex-column">
										<div
											className="rounded-3 bg-success-subtle d-flex align-items-center justify-content-center mb-4"
											style={{ height: '10rem' }}
										>
											<i className={`bi ${item.icon} text-success`} style={{ fontSize: '3rem' }} aria-hidden="true" />
										</div>
										<h3 className="h4 fw-bold text-success-emphasis mb-2">
											{t(item.titleKey, item.titleDefault)}
										</h3>
										<p className="text-muted mb-4 flex-grow-1">
											{t(item.descKey, item.descDefault)}
										</p>
										<div className="d-flex flex-wrap gap-2">
											<a
												href={item.file}
												target="_blank"
												rel="noopener noreferrer"
												className="btn btn-success"
												onClick={() => trackResourceAction('view', item.id)}
											>
												<i className="bi bi-eye me-2" aria-hidden="true" />
												{t('resources.brochure.view', 'View PDF')}
											</a>
											<a
												href={item.file}
												download
												className="btn btn-outline-success"
												onClick={() => trackResourceAction('download', item.id)}
											>
												<i className="bi bi-download me-2" aria-hidden="true" />
												{t('resources.brochure.download', 'Download')}
											</a>
										</div>
									</div>
								</div>
							</div>
						))}
					</div>
				</div>
			</section>

			<section className="landing-section py-5 bg-white">
				<div className="container">
					<div className="cta-tile px-4 py-5 shadow-lg position-relative overflow-hidden">
						<div className="row align-items-center g-4">
							<div className="col-lg-8">
								<h2 className="display-6 fw-bold text-success-emphasis mb-3">
									{t('resources.cta.title', 'Want to see HortEra in action?')}
								</h2>
								<p className="text-muted mb-0">
									{t(
										'resources.cta.subtitle',
										'Explore completed garden projects or try the questionnaire to start your own plan.'
									)}
								</p>
							</div>
							<div className="col-lg-4 d-flex flex-column flex-sm-row gap-3 justify-content-lg-end">
								<Link to="/gallery" className="btn btn-success btn-lg">
									{t('resources.cta.gallery', 'View gallery')}
								</Link>
								<Link to="/try-questionnaire" className="btn btn-outline-success btn-lg">
									{t('resources.cta.questionnaire', 'Try questionnaire')}
								</Link>
							</div>
						</div>
					</div>
				</div>
			</section>
		</>
	);
}
